export type CustomerInvoicePortal = "hire" | "seek";

export type CustomerInvoiceOwnerType = "organization" | "candidate";

const CUSTOMER_INVOICE_NUMBER = /^(FHB2B|FHB2C|FSB2C)\/(\d{4})\/(\d{5,})$/;
const CUSTOMER_INVOICE_SLUG = /^(FHB2B|FHB2C|FSB2C)-(\d{4})-(\d{5,})$/;

export function officialInvoiceNumberToSlug(invoiceNumber: string): string {
  const match = CUSTOMER_INVOICE_NUMBER.exec(invoiceNumber.trim().toUpperCase());
  if (!match) throw new Error("official_invoice_number_invalid");
  return `${match[1]}-${match[2]}-${match[3]}`;
}

export function officialInvoiceSlugToNumber(slug: string): string | null {
  const match = CUSTOMER_INVOICE_SLUG.exec(slug.trim().toUpperCase());
  return match ? `${match[1]}/${match[2]}/${match[3]}` : null;
}

export function buildCustomerInvoicePath(invoiceNumber: string): string {
  return `/invoices/${officialInvoiceNumberToSlug(invoiceNumber)}`;
}

export function isCustomerInvoiceOwnerAuthorized(input: {
  portal: CustomerInvoicePortal;
  authenticatedUserId: string;
  ownerType: string;
  ownerId: string;
  activeOrganizationIds?: readonly string[];
}): boolean {
  if (input.portal === "seek") {
    return input.ownerType === "candidate" &&
      input.ownerId === input.authenticatedUserId;
  }

  return input.ownerType === "organization" &&
    (input.activeOrganizationIds ?? []).includes(input.ownerId);
}

export interface OfficialInvoiceCurrentPointer {
  invoice_id: string;
  artifact_type: string;
  environment: string;
  artifact_id: string;
}

export interface OfficialInvoicePdfArtifact {
  id: string;
  invoice_id: string;
  erp_invoice_map_id: string;
  odoo_invoice_id: string;
  artifact_type: string;
  environment: string;
  artifact_state: string;
  storage_bucket: string;
  storage_key: string;
  content_type: string;
}

/**
 * Customer delivery follows only the governed approved-current pointer. A
 * newer immutable revision can exist for finance review without becoming
 * customer-visible.
 */
export function isApprovedCurrentOfficialPdf(input: {
  invoiceId: string;
  erpInvoiceMapId: string;
  odooInvoiceId: string;
  environment: string;
  pointer: OfficialInvoiceCurrentPointer | null;
  artifact: OfficialInvoicePdfArtifact | null;
  reviewState: string | null;
}): boolean {
  const { pointer, artifact } = input;
  return Boolean(
    pointer &&
      artifact &&
      pointer.invoice_id === input.invoiceId &&
      pointer.artifact_type === "official_pdf" &&
      pointer.environment === input.environment &&
      pointer.artifact_id === artifact.id &&
      artifact.invoice_id === input.invoiceId &&
      artifact.erp_invoice_map_id === input.erpInvoiceMapId &&
      artifact.odoo_invoice_id === input.odooInvoiceId &&
      artifact.artifact_type === "official_pdf" &&
      artifact.environment === input.environment &&
      artifact.artifact_state === "stored" &&
      artifact.storage_bucket &&
      artifact.storage_key &&
      artifact.content_type === "application/pdf" &&
      input.reviewState === "approved",
  );
}

export function officialInvoiceFilename(invoiceNumber: string): string {
  return `${officialInvoiceNumberToSlug(invoiceNumber)}.pdf`;
}
